import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { PiletApi } from '@chess/platform-web';

// The home screen, the app's default (2026-10-08; "Game stats" at /game
// before, which still opens it): one game at a glance. Under the top bar
// ("Home" and Sync) the game strip (`shared.gameStrip`, a tile per game of
// the last 30 days; the game bar until 2026-10-08), then the selected game (`?game=`, the latest
// without one): its card (plugin `home-card-latest-game`, on `game.card`) and
// its opening, middlegame and ending sections (plugin `shared-last-game`, on
// `game.sections`, orders 10–12). One column of sections bound to this
// screen's slot. The rail entry opens `/` without a selection, i.e. the
// most recent game, and so does the "Latest game" button before Sync
// (`game.headerActions`), as on Overview; on / the button replaces the entry
// rather than stacking one. The title is plain, like every screen's.
const bind = { sections: 'game.sections' };

// Phosphor house, the home entry on the navigation rail.
const HOUSE =
  'M218.83 103.77l-80-75.48a1.14 1.14 0 0 1-.11-.11 16 16 0 0 0-21.53 0l-.11.11-79.91 75.48A16 16 0 0 0 32 115.55V208a16 16 0 0 0 16 16h48a16 16 0 0 0 16-16v-48h32v48a16 16 0 0 0 16 16h48a16 16 0 0 0 16-16v-92.45a16 16 0 0 0-5.17-11.78ZM208 208h-48v-48a16 16 0 0 0-16-16h-32a16 16 0 0 0-16 16v48H48v-92.45l.11-.1L128 40l79.9 75.43.11.1Z';

export function setup(app: PiletApi) {
  const Page = () => <app.Extension name="layout" params={{ layout: 'game', bind }} />;
  app.registerPage('/', Page);
  // The old address keeps working (bookmarks, links in the docs).
  app.registerPage('/game', Page);
  app.registerExtension('rail.entries', () => null, { id: 'home', title: 'Home', to: '/', icon: HOUSE, order: 10 });
  app.registerExtension('game.sections', () => <app.Extension name="shared.dashboardHeader" params={{ screen: 'Home', period: null, actions: 'game.headerActions' }} />, { order: 1 });
  // Just before Sync: back to the most recent game, replacing the entry when
  // already on / so stepping back through history isn't lengthened.
  const LatestGame: React.FC = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    return (
      <a
        className="btn btn-secondary"
        href="/"
        data-track="header.latest-game"
        title="Show the most recent game"
        onClick={(e) => {
          // A modified click (new tab, new window) is the browser's.
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          navigate('/', { replace: pathname === '/' || pathname === '/game' });
        }}>
        Latest game
      </a>
    );
  };
  app.registerExtension('game.headerActions', LatestGame, { order: 10 });
  // The game strip (one tile per game of the period, `shared-game-bar`), then
  // the selected game's title and card.
  app.registerExtension('game.sections', () => <app.Extension name="shared.gameStrip" />, { order: 2 });
  // The game's title (3, `home-card-latest-game`) comes between them.
  app.registerExtension('game.sections', () => <app.Extension name="dashboard.section" params={{ slot: 'game.card' }} />, { order: 4 });
  // Then the selected game's stage sections (`shared-last-game`, orders 10–12).
}
